import React, { useState, useRef, useEffect, Suspense, forwardRef } from 'react'
import loadable from '@loadable/component'
import { connect, useDispatch } from 'react-redux'
import classnames from 'classnames'
import { useLocation, useNavigate } from 'react-router-dom'
import { useTranslation } from 'react-i18next'
import { get } from 'lodash'
import { motion, AnimatePresence } from 'framer-motion'

import { settings } from '@krea/common/settings'
import { COUNTRY_CODE } from '@krea/common/utils'
import { useMobile } from '@krea/common/hooks'
import { setIsPrivateRoute } from '@krea/common/store/general/actions'
import { Button, Container } from '@krea/common/shared-components'

import Logo from '@krea/common/shared-components/app/logo'
import Language from '@krea/common/shared-components/app/language'
import UserSVG from '@krea/common/images/svg/user.svg'
import CloseSVG from '@krea/common/images/svg/close.svg'

import styles from './Header.module.scss'

const SVG = loadable(() => import('react-inlinesvg'))

export const MenuContext = React.createContext({
	toggleMenu: () => {},
	showSteps: () => {},
	stepsIsVisible: false
})

const motionBackdrop = {
	hidden: {
		opacity: 0
	},
	visible: {
		opacity: 1
	}
}

const motionMenu = {
	hidden: {
		opacity: 0,
		transitionEnd: {
			visibility: 'hidden',
			zIndex: 0
		},
		transition: {
			duration: 0.4,
			ease: 'easeOut'
		}
	},
	visible: {
		opacity: 1,
		visibility: 'visible',
		zIndex: 10001,
		transition: {
			duration: 0.4,
			ease: 'easeOut'
		}
	}
}

const logoStyling = (countrycode) => {
	switch (countrycode) {
		case COUNTRY_CODE.SE:
			return styles.kreaLogo
		case COUNTRY_CODE.FI:
			return styles.kreditaLogo
		default:
			return styles.kreaLogo
	}
}

const MenuButton = forwardRef(({ className, onClick, icon }, ref) => {
	return (
		<button
			ref={ref}
			className={classnames(styles.menuButton, 'd-flex justify-content-center align-items-center p-2', className)}
			onClick={onClick}
			aria-label="Menu"
		>
			<div className={classnames(styles.menuIcon, 'position-relative')}>
				<SVG className="position-absolute w-100 h-auto" src={icon} />
			</div>
		</button>
	)
})

const MobileMenuDrawer = forwardRef(({ onClose, children, fullScreen }, ref) => {
	return (
		<div
			ref={ref}
			className={classnames(styles.mobileMenu, 'd-flex flex-column position-fixed w-100 mh-100 bg-white', {
				'h-100': fullScreen
			})}
		>
			<div className={classnames(styles.height, 'flex-grow-0 flex-shrink-0 w-100 border-bottom')}>
				<Container fluid className="h-100">
					<div className="d-flex justify-content-between align-items-center h-100 text-primary">
						<Logo className={classnames(styles.logo, logoStyling(settings.countryCode))} />

						<MenuButton icon={CloseSVG} className="mr-n2" onClick={onClose} />
					</div>
				</Container>
			</div>

			<div className="flex-grow-1 flex-shrink-1 w-100 overflow-auto">
				<Container>
					<div className="py-6 w-100">{children}</div>
				</Container>
			</div>
		</div>
	)
})

const Header = ({ user, privateRoutes, isPrivateRoute, MiddleNavbarComponent, logout, applicationSelector }) => {
	const { t } = useTranslation()

	const refHeader = useRef(null)
	const navRef = useRef(null)
	const refMenu = useRef(null)

	const [sticky, setSticky] = useState(false)
	const [mobileMenu, setMobileMenu] = useState(false)
	const [stepsMenu, setStepsMenu] = useState(false)
	const [scrollDown, setScrollDown] = useState(false)

	const isMobile = useMobile()

	const dispatch = useDispatch()
	const { pathname } = useLocation()
	const navigate = useNavigate()

	const isDelegate = pathname === '/delegated-information'

	const isNotLoginPage = pathname !== '/login'
	const isNotPreliminaryBidForm = pathname !== '/direct/preliminary-bid-form'

	const mainLogoHref = isPrivateRoute ? '/' : settings.countryWebsiteUrl

	useEffect(() => {
		let isPrivateRoute = !!privateRoutes.includes(pathname)
		dispatch(setIsPrivateRoute(isPrivateRoute))
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [pathname, privateRoutes])

	const openMenu = () => {
		setMobileMenu(!mobileMenu)
	}

	const toggleStepsMenu = () => {
		setStepsMenu(!stepsMenu)
	}

	const onLogout = () => {
		dispatch(logout())
	}

	useEffect(() => {
		const scrollCalc = () => {
			if (refHeader.current && refHeader.current.offsetHeight) {
				window.pageYOffset > refHeader.current.offsetHeight ? setSticky(true) : setSticky(false)
			}
		}

		scrollCalc()
		window.addEventListener('scroll', scrollCalc)

		return () => {
			window.removeEventListener('scroll', scrollCalc)
		}
	}, [])

	useEffect(() => {
		let lastScrollTop = 0

		const UpDownScroll = () => {
			let st = window.pageYOffset || document.documentElement.scrollTop

			if (st > lastScrollTop) {
				// downscroll code
				setScrollDown(true)
			} else {
				// upscroll code
				setScrollDown(false)
			}
			lastScrollTop = st <= 0 ? 0 : st // For Mobile or negative scrolling
		}

		if (sticky) {
			UpDownScroll()
			window.addEventListener('scroll', UpDownScroll)
		}

		return () => {
			window.removeEventListener('scroll', UpDownScroll)
		}
	}, [sticky])

	useEffect(() => {
		function handleClickOutside(event) {
			if (mobileMenu) {
				if (refMenu.current && !refMenu.current.contains(event.target)) {
					openMenu()
				}
			}

			if (stepsMenu) {
				if (navRef.current && !navRef.current.contains(event.target)) {
					toggleStepsMenu()
				}
			}
		}
		window.addEventListener('mousedown', handleClickOutside)
		window.addEventListener('scroll', handleClickOutside)

		return () => {
			window.removeEventListener('mousedown', handleClickOutside)
			window.removeEventListener('scroll', handleClickOutside)
		}
	})

	const steps = !isDelegate && isNotPreliminaryBidForm && (
		<MenuContext.Provider value={{ showSteps: toggleStepsMenu, stepsIsVisible: stepsMenu }}>
			<Suspense fallback={<></>}>{MiddleNavbarComponent ? <MiddleNavbarComponent /> : null}</Suspense>
		</MenuContext.Provider>
	)

	const showBackdrop = mobileMenu || stepsMenu

	return (
		<>
			<header
				ref={refHeader}
				className={classnames(
					styles.root,
					styles.height,
					'position-fixed flex-grow-0 flex-shrink-0 w-100 bg-white border-bottom',
					{
						[styles.sticky]: sticky,
						[styles.down]: scrollDown
					}
				)}
			>
				<Container fluid className="h-100">
					<div className="d-flex align-items-center justify-content-between h-100 text-primary">
						<Logo className={classnames(styles.logo, logoStyling(settings.countryCode))} href={mainLogoHref} />

						<div className="d-flex align-items-center">
							{!isDelegate && (
								<>
									<Language user={user} />

									{!isMobile && isNotLoginPage && user?.id && applicationSelector}

									{isNotLoginPage && !user?.id && (
										<Button
											size={isMobile ? 'sm' : 'md'}
											variant="outlined"
											className={classnames(styles.login, 'ml-xl-4')}
											onClick={() => {
												navigate('/login')
											}}
										>
											{t('menu.login')}
										</Button>
									)}

									{!isMobile && user?.id && (
										<Button variant="outlined" className="ml-4" style={{ width: '8.75rem' }} onClick={onLogout}>
											{t('menu.logout')}
										</Button>
									)}

									{isMobile && isNotLoginPage && user?.id && (
										<MenuButton className="mr-n2" icon={UserSVG} onClick={openMenu} />
									)}
								</>
							)}
						</div>
					</div>
				</Container>
			</header>

			{isNotLoginPage && !isDelegate && isNotPreliminaryBidForm && (
				<>
					<nav
						ref={navRef}
						className={classnames(
							styles.navigation,
							'd-flex flex-column flex-xl-row position-fixed w-100 mh-100 bg-white border-bottom',
							{
								[styles.sticky]: sticky || stepsMenu,
								[styles.down]: scrollDown,
								'border-white': sticky || stepsMenu
							}
						)}
					>
						{!isMobile ? (
							<div className="h-100 d-flex justify-content-center w-100">
								<Container className="h-100">
									<div className="h-100 d-flex justify-content-center">{steps}</div>
								</Container>
							</div>
						) : (
							<>{steps}</>
						)}
					</nav>

					{isMobile && (
						<>
							<motion.div variants={motionMenu} initial="hidden" animate={mobileMenu ? 'visible' : 'hidden'}>
								<MobileMenuDrawer ref={refMenu} onClose={openMenu}>
									<div className="d-flex flex-column flex-md-row align-items-center bg-gray rounded p-4">
										{isNotLoginPage && user?.id && applicationSelector && (
											<MenuContext.Provider value={{ toggleMenu: openMenu }}>
												{applicationSelector}
											</MenuContext.Provider>
										)}

										{user?.id && (
											<div className="flex-shrink-0">
												<Button variant="text" className="px-4" onClick={onLogout}>
													{t('menu.logout')}
												</Button>
											</div>
										)}
									</div>
								</MobileMenuDrawer>
							</motion.div>
						</>
					)}

					<AnimatePresence>
						{showBackdrop && (
							<motion.div
								variants={motionBackdrop}
								initial="hidden"
								animate="visible"
								exit="hidden"
								transition={{
									default: { duration: 0.4, ease: 'easeOut' }
								}}
								className={classnames(styles.backdrop, 'position-fixed w-100 vh-100')}
							/>
						)}
					</AnimatePresence>
				</>
			)}
		</>
	)
}

const mapStateToProps = (state) => ({
	user: get(state.userReducer, 'user'),
	isPrivateRoute: get(state.generalReducer, 'isPrivateRoute'),
	privateRoutes: get(state.generalReducer, 'privateRoutes', [])
})

export default connect(mapStateToProps)(Header)
